// react
import { SyntheticEvent, useContext, useState } from 'react';

// types
import {
	Account,
	AccountEntity,
	Document,
	EntityType,
	Pagination,
	Payment,
	Transaction,
} from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Box, Chip, Grid, Paper, Tooltip } from '@mui/material';
import { TabContext, TabPanel } from '@mui/lab';

// router
import { useSearchParams } from 'react-router-dom';

// ui
import { Amount } from '../../ui/Amount/Amount';
import { BtnExpand } from '../../ui/BtnExpand/BtnExpand';
import { BtnMenu } from '../../ui/BtnMenu/BtnMenu';
import { ChipStatus } from '../../ui/ChipStatus/ChipStatus';
import { IconCircleProps } from '../../ui/IconCircle/IconCircle';
import { IconCircleTransaction } from '../../ui/IconCircleTransaction/IconCircleTransaction';
import { IconList } from '../../ui/IconList/IconList';
import { MenuItemProps } from '../../ui/Menu/Menu';
import { RowKeyValue } from '../../ui/RowKeyValue/RowKeyValue';
import { TabProps, Tabs } from '../../ui/Tabs/Tabs';
import { Tags } from '../../ui/Tags/Tags';
import { Time } from '../../ui/Time/Time';
import { TypographyCaption } from '../../ui/TypographyCaption/TypographyCaption';
import { UserAvatarModified } from '../../ui/UserAvatarModified/UserAvatarModified';

// tables
import { TableBasicComments } from '../../tables/basic/TableBasicComments/TableBasicComments';
import { TableBasicDocs } from '../../tables/basic/TableBasicDocs/TableBasicDocs';
import { TableBasicPayments } from '../../tables/basic/TableBasicPayments/TableBasicPayments';

// theme
import { theme } from '../../../theme';

// context
import { ContextUserProjectPermissions } from '../../pages/Project/Project.context';

// links
import { LinkAccount } from '../../links/LinkAccount/LinkAccount';
import { LinkEntity } from '../../links/LinkEntity/LinkEntity';

// dialogs
import { DialogAddComment } from '../../dialogs/DialogAddComment/DialogAddComment';
import { DialogUploadDocs } from '../../dialogs/DialogUploadDocs/DialogUploadDocs';
import { DialogUpdateTransactionStatus } from '../../dialogs/DialogUpdateTransactionStatus/DialogUpdateTransactionStatus';

// icons
import {
	AddCommentOutlined as IconAddComment,
	InsertDriveFileOutlined as IconInsertDriveFile,
	RestoreOutlined as IconRestore,
} from '@mui/icons-material';

// utils
import { defaultPagination } from '../../../utils/defaults';
import { enhanceComments, enhanceDocuments } from '../../../utils/transactions';
import { translateEntityType } from '../../../utils/translations';

// props
export interface CardTransferProps {
	refetchTransactions?: () => void;
	transaction: Transaction;
}

export const CardTransfer = ({ refetchTransactions, transaction }: CardTransferProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// context
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// hooks
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramDialog = searchParams.get('dialog');
	const paramDialogTransaction = searchParams.get('dialogTransaction');

	// state
	const [documents, setDocuments] = useState<Document[]>([]);
	const [isExpanded, setIsExpanded] = useState(false);
	const [paginationDocuments, setPaginationDocuments] = useState<Pagination>(defaultPagination);
	const [tabValue, setTabValue] = useState('financing');

	// fetch documents
	const { isLoading: isLoadingDocs, refetch: refetchDocuments } = useFetch({
		isEnabled: Boolean(transaction),
		url: `${process.env.REACT_APP_API_URL}/documents?transaction=${transaction?.id}&limit=10&page=1&fields=created,notarizationHash,id,name,type,user&populate=type,user`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				const documentsEnhanced = enhanceDocuments({ documents: res.data });
				setDocuments(documentsEnhanced);
			}

			if (res.pagination) {
				setPaginationDocuments(res.pagination);
			}
		},
	});

	// handlers
	const handleToggleCard = () => {
		setIsExpanded(!isExpanded);
	};

	const handleChangeTab = (e: SyntheticEvent, value: string) => {
		setTabValue(value);
	};

	const handleCloseDialog = () => {
		searchParams.delete('dialog');
		searchParams.delete('dialogTransaction');
		setSearchParams(searchParams);
	};

	// vars
	const isReverted = transaction.status === 'REVERTED';
	const transactionFromAccount = transaction.fromAccount as Account;
	const transactionToAccount = transaction.toAccount as Account;
	const transactionFromAccountEntity = (transactionFromAccount.entities as AccountEntity[]).find(
		(e) => e.relationship === 'OWNER'
	)?.entity;
	const transactionToAccountEntity = (transactionToAccount.entities as AccountEntity[]).find(
		(e) => e.relationship === 'OWNER'
	)?.entity;

	const tabs: TabProps[] = [
		{
			label: t('financing'),
			value: 'financing',
		},
	];

	if (userProjectPermissions?.document?.read) {
		tabs.push({
			label: t('documents'),
			value: 'documents',
		});
	}

	if (userProjectPermissions?.comment?.read) {
		tabs.push({
			label: t('comments'),
			value: 'comments',
		});
	}

	if (userProjectPermissions?.tag?.read) {
		tabs.push({
			label: t('tags'),
			value: 'tags',
		});
	}

	const btnMenuItems: MenuItemProps[] = [];
	const propsMenuItemRevert: Partial<MenuItemProps> = {
		children: t('revertTransfer'),
	};

	const hasRevert =
		!isReverted && transaction.type === 'TRANSFER' && userProjectPermissions?.transfer?.update;

	if (userProjectPermissions?.document?.create) {
		btnMenuItems.push({
			children: t('uploadDocuments'),
			icon: <IconInsertDriveFile />,
			onClick: () => {
				searchParams.set('dialog', 'uploadDocsTransaction');
				searchParams.set('dialogTransaction', String(transaction.id));
				setSearchParams(searchParams);
			},
		});
	}

	if (userProjectPermissions?.comment?.create) {
		btnMenuItems.push({
			children: t('addComment'),
			icon: <IconAddComment />,
			onClick: () => {
				searchParams.set('dialog', 'addComment');
				searchParams.set('dialogTransaction', String(transaction.id));
				setSearchParams(searchParams);
			},
		});
	}

	if (hasRevert) {
		btnMenuItems.push({
			icon: <IconRestore color="error" />,
			onClick: () => {
				searchParams.set('dialog', 'revertTransaction');
				searchParams.set('dialogTransaction', String(transaction.id));
				setSearchParams(searchParams);
			},
			...propsMenuItemRevert,
		});
	}

	const iconCircleProps: IconCircleProps = {
		TooltipProps: {
			title: t('transfer'),
		},
		type: 'transfer',
	};

	const comments = enhanceComments({ comments: transaction.comments });

	// filter new comments
	const commentsNew = comments.filter((c) => c.isNew);

	// filter new documents
	const documentsNew = documents.filter((d) => d.isNew);

	if (documentsNew.length || commentsNew.length) {
		iconCircleProps.badges = [
			{
				badgeContent: documentsNew.length || commentsNew.length,
				color: 'info',
				component: 'div',
				overlap: 'circular',
				TooltipProps: documentsNew.length
					? { title: t('newDocuments') }
					: { title: t('newComments') },
			},
		];

		const chipNew = <Chip color="error" label={t('new')} size="small" variant="outlined" />;

		if (documentsNew.length) {
			const tabDocs = tabs.find((tab) => tab.value === 'documents');

			if (tabDocs) {
				tabDocs.label = (
					<Grid alignItems="center" container spacing={1}>
						<Grid item>{tabDocs.label}</Grid>
						<Grid item>{chipNew}</Grid>
					</Grid>
				);
			}
		}

		if (commentsNew.length) {
			const tabComments = tabs.find((tab) => tab.value === 'comments');

			if (tabComments) {
				tabComments.label = (
					<Grid alignItems="center" container spacing={1}>
						<Grid item>{tabComments.label}</Grid>
						<Grid item>{chipNew}</Grid>
					</Grid>
				);
			}
		}
	}

	const payments: Payment[] = [];

	// add transaction to each payment
	for (let p = 0; p < transaction.payments.length; p++) {
		payments.push({
			...transaction.payments[p],
			transaction,
		});
	}

	return (
		<>
			<Paper
				sx={(theme) => {
					const borderColor = isExpanded ? theme.palette.brand.blue[500] : 'transparent';
					return {
						border: `1px ${borderColor} solid`,
						position: 'relative',
					};
				}}>
				<Box sx={{ padding: theme.spacing(1.5) }}>
					<Grid container alignItems={'center'} spacing={{ xs: 1, md: 2 }}>
						<Grid item xs={12} lg={1.5}>
							<Box
								sx={{
									margin: theme.spacing(0, 0, 2, 0),
									[theme.breakpoints.up('lg')]: {
										margin: 0,
									},
								}}>
								<Grid alignItems="center" container spacing={2} wrap="nowrap">
									<Grid item>
										<IconCircleTransaction transaction={transaction} {...iconCircleProps} />
									</Grid>
									<Grid item>
										<Grid container direction="column">
											<Grid item>
												<TypographyCaption id={transaction.id} />
											</Grid>
											<Grid item>
												<Time date={transaction.date} />
											</Grid>
										</Grid>
									</Grid>
								</Grid>
							</Box>
						</Grid>
						<Grid item xs={12} lg={1.5}>
							<RowKeyValue
								label={t('from')}
								value={
									<IconList
										GridItemProps={{
											xs: 12,
										}}
										GridItemIconProps={{
											display: {
												xs: 'none',
												lg: 'inline-flex',
											},
										}}
										GridItemValueProps={{
											sx: {
												overflow: 'hidden',
												textOverflow: 'ellipsis',
												whiteSpace: {
													lg: 'nowrap',
												},
											},
										}}
										list={[
											{
												IconCircleProps: {
													TooltipProps: {
														title: translateEntityType(
															transactionFromAccountEntity?.type as EntityType,
															t
														),
													},
													type: 'entity',
												},
												value: <LinkEntity entity={transactionFromAccountEntity} />,
											},
											{
												IconCircleProps: {
													TooltipProps: {
														title: t('account'),
													},
													type: 'account',
												},
												value: <LinkAccount account={transactionFromAccount} />,
											},
										]}
									/>
								}
							/>
						</Grid>
						<Grid item xs={12} lg={1.5}>
							<RowKeyValue
								label={t('sent')}
								value={
									<Grid container alignItems="center" spacing={1}>
										<Grid item xs="auto">
											<Amount currency={transaction.currency} value={transaction.amount} />
										</Grid>
										{transaction.status === 'REVERTED' && (
											<Grid item xs="auto">
												<Tooltip arrow placement="top" title={t('reverted')}>
													<IconRestore color="error" />
												</Tooltip>
											</Grid>
										)}
									</Grid>
								}
							/>
						</Grid>
						<Grid item xs={12} lg={1.5}>
							<RowKeyValue label={t('status')} value={<ChipStatus status={transaction.status} />} />
						</Grid>
						<Grid item xs={12} lg={1.5}>
							<RowKeyValue
								label={t('to')}
								value={
									<IconList
										GridItemProps={{
											xs: 12,
										}}
										GridItemIconProps={{
											display: {
												xs: 'none',
												lg: 'inline-flex',
											},
										}}
										GridItemValueProps={{
											sx: {
												overflow: 'hidden',
												textOverflow: 'ellipsis',
												whiteSpace: {
													lg: 'nowrap',
												},
											},
										}}
										list={[
											{
												IconCircleProps: {
													TooltipProps: {
														title: translateEntityType(
															transactionToAccountEntity?.type as EntityType,
															t
														),
													},
													type: 'entity',
												},
												value: <LinkEntity entity={transactionToAccountEntity} />,
											},
											{
												IconCircleProps: {
													TooltipProps: {
														title: t('account'),
													},
													type: 'account',
												},
												value: <LinkAccount account={transactionToAccount} />,
											},
										]}
									/>
								}
							/>
						</Grid>
						<Grid item xs={12} lg={1.5}>
							<RowKeyValue
								label={t('received')}
								value={
									<Amount
										currency={transaction.currencyReceived || transaction.currency}
										hasAsterisk={!transaction.currencyReceived || !transaction.amountReceived}
										value={transaction.amountReceived || transaction.amount}
									/>
								}
							/>
						</Grid>
						<Grid item xs={12} lg={1.5}>
							<RowKeyValue
								label={t('updated')}
								value={
									<UserAvatarModified
										transaction={transaction}
										activityTypes={['TRANSACTION_UPDATED', 'TRANSACTION_CREATED']}
									/>
								}
							/>
						</Grid>
						<Grid item xs={12} lg={1}>
							<IconList
								list={[
									{
										IconCircleProps: {
											TooltipProps: {
												title: t('documents'),
											},
											type: 'document',
										},
										value: documents.length,
									},
									{
										IconCircleProps: {
											TooltipProps: {
												title: t('comments'),
											},
											type: 'comment',
										},
										value: comments.length,
									},
								]}
							/>
						</Grid>
						<Grid item xs={12} lg={0.5}>
							<Grid alignItems="center" container justifyContent={{ lg: 'flex-end' }} spacing={1}>
								{btnMenuItems.length > 0 && (
									<Grid
										item
										sx={{
											position: 'absolute',
											top: 0,
											right: theme.spacing(1),
											[theme.breakpoints.up('lg')]: {
												position: 'relative',
												top: 'auto',
												right: 'auto',
											},
										}}>
										<BtnMenu items={btnMenuItems} />
									</Grid>
								)}
								<Grid item>
									<BtnExpand
										isExpanded={isExpanded}
										label={isExpanded ? t('hideDetails') : t('seeDetails')}
										onClick={handleToggleCard}
									/>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Box>
				{isExpanded && (
					<TabContext value={tabValue}>
						<Tabs
							BoxProps={{
								sx: {
									marginTop: {
										lg: theme.spacing(3),
									},
								},
							}}
							hasBorder={false}
							onChange={handleChangeTab}
							tabs={tabs}
							value={tabValue}
						/>
						<TabPanel value="financing">
							<TableBasicPayments
								data={payments}
								headersIncluded={[
									'amount',
									'hash',
									'received',
									'recorded',
									'source',
									'status',
									'total',
								]}
								transaction={transaction}
							/>
						</TabPanel>
						<TabPanel value="documents">
							<TableBasicDocs
								data={documents}
								isLoading={isLoadingDocs}
								pagination={paginationDocuments}
								refetchDocuments={refetchDocuments}
							/>
						</TabPanel>
						<TabPanel value="comments">
							<TableBasicComments data={transaction.comments} />
						</TabPanel>
						<TabPanel value="tags">
							<Tags tags={transaction.tags} />
						</TabPanel>
					</TabContext>
				)}
			</Paper>
			<DialogAddComment
				isOpen={paramDialog === 'addComment' && paramDialogTransaction === String(transaction.id)}
				onClose={handleCloseDialog}
				onSuccess={() => {
					if (refetchTransactions) {
						refetchTransactions();
					}
				}}
			/>
			<DialogUploadDocs
				isOpen={
					paramDialog === 'uploadDocsTransaction' &&
					paramDialogTransaction === String(transaction.id)
				}
				onClose={handleCloseDialog}
				onSuccess={() => {
					if (refetchTransactions) {
						refetchTransactions();
					}
				}}
				transaction={transaction}
			/>
			{paramDialog === 'revertTransaction' && paramDialogTransaction === String(transaction.id) && (
				<DialogUpdateTransactionStatus
					isOpen={true}
					onClose={handleCloseDialog}
					onSuccess={refetchTransactions}
					status="REVERTED"
					type={transaction.type}
				/>
			)}
		</>
	);
};
